import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ShoppingCart, ArrowRight, Store, Loader2, X, Filter, Search } from "lucide-react";
import { StoreHeader, StoreFooter, CartSheet, TrustBadges } from "@/components/store";
import { useCart } from "../../context/CartContext";
import { useStore } from "../../hooks/useStore";
import { getImageUrl } from "../../utils/imageUtils";
import { cn } from "@/lib/utils";
import { storePath, getSubdomain } from "../../utils/storeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

// Default theme (fallback)
const DEFAULT_THEME = {
    primary_color: "#1a365d",
    secondary_color: "#2c5282",
    accent_color: "#d69e2e",
    background_color: "#ffffff",
    text_color: "#1a202c",
    font_family: "Cormorant Garamond, serif"
};

export default function StoreShop() {
    const { subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    const navigate = useNavigate();
    const location = useLocation();

    // Cart Context for Header
    const { cartCount, setCartOpen } = useCart();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Store & Theme from Hook
    const { 
        store, 
        theme, 
        storeAppearance, 
        platformInfo, 
        collections, 
        loading: storeLoading 
    } = useStore(subdomain);

    // Shop State
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // Filters
    const [activeCollection, setActiveCollection] = useState(null);
    const [activeCollectionName, setActiveCollectionName] = useState("");
    const [activeSource, setActiveSource] = useState("all"); // 'all', 'admin', 'custom'
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [collectionSearch, setCollectionSearch] = useState("");

    // Sorted and filtered collections
    const filteredCollections = useMemo(() => {
        return (collections || [])
            .filter(c => c.is_active !== false)
            .filter(c => c.name.toLowerCase().includes(collectionSearch.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [collections, collectionSearch]);

    // Handle URL Params for Collection and Search and trigger fetch
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const colId = searchParams.get("collection_id") || null;
        const q = searchParams.get("search") || "";

        // Sync state
        setActiveCollection(colId);
        setSearchTerm(q);

        // Reset and Fetch immediately
        setProducts([]);
        setPage(1);
        setHasMore(true);
        fetchProducts(1, colId, activeSource, q);
    }, [location.search, activeSource]); // source is internal state, search is URL state

    // Update activeCollectionName
    useEffect(() => {
        if (activeCollection && collections?.length > 0) {
            const col = collections.find(c => c._id === activeCollection || c.collection_id === activeCollection);
            if (col) {
                setActiveCollectionName(col.name);
            }
        } else if (!activeCollection) {
            setActiveCollectionName("");
        }
    }, [activeCollection, collections]);

    // Infinite Scroll Ref
    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Fetch Next Page
    useEffect(() => {
        if (page > 1) {
            fetchProducts(page, activeCollection, activeSource, searchTerm);
        }
    }, [page]);

    const fetchProducts = async (pageNum, collectionId, source = "all", keyword = "") => {
        setLoading(true);
        try {
            const limit = 12;
            let url = `${API}/public/store/${subdomain}/products?limit=${limit}&page=${pageNum}`;

            if (collectionId) {
                url += `&collection_id=${collectionId}`;
            }

            if (source !== "all") {
                url += `&source=${source}`;
            }

            if (keyword) {
                url += `&keyword=${encodeURIComponent(keyword)}`;
            }

            const response = await publicAxios.get(url);
            const newProducts = response.data.products || [];
            const total = response.data.total || 0;

            setProducts(prev => pageNum === 1 ? newProducts : [...prev, ...newProducts]);
            setTotalProducts(total);
            const fetchedSoFar = (pageNum - 1) * limit + newProducts.length;
            setHasMore(fetchedSoFar < total && newProducts.length > 0);

        } catch (error) {
            console.error("Failed to fetch products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCollectionClick = (collection) => {
        const id = collection.collection_id || collection._id;
        const searchParams = new URLSearchParams(location.search);

        if (activeCollection === id) {
            searchParams.delete("collection_id");
        } else {
            searchParams.set("collection_id", id);
        }

        const newSearch = searchParams.toString();
        navigate(newSearch ? storePath(subdomain, `/shop?${newSearch}`) : storePath(subdomain, `/shop`));
        setIsSidebarOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearFilter = () => {
        setActiveCollection(null);
        setActiveCollectionName("");
        setActiveSource("all");
        setSearchTerm("");
        // Also clear URL search params
        navigate(storePath(subdomain, `/shop`));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!store || storeLoading) {
        return (
            <div className="min-h-screen bg-white">
                {/* Skeleton Header */}
                <div className="h-16 border-b flex items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg animate-shimmer" />
                        <div className="w-32 h-6 rounded animate-shimmer" />
                    </div>
                </div>

                {/* Skeleton Hero */}
                <div className="w-full h-32 animate-shimmer mb-8" />

                <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 flex gap-8">
                    {/* Skeleton Sidebar */}
                    <div className="hidden md:block w-64 space-y-4">
                        <div className="h-10 rounded animate-shimmer" />
                        <div className="h-64 rounded animate-shimmer" />
                    </div>

                    {/* Skeleton Products */}
                    <div className="flex-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="space-y-3">
                                    <div className="aspect-[4/5] rounded-xl animate-shimmer" />
                                    <div className="w-3/4 h-4 rounded animate-shimmer" />
                                    <div className="w-1/2 h-4 rounded animate-shimmer" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">

            {/* Shop Hero/Header */}
            <div className="py-8 lg:py-12 px-4 relative overflow-hidden transition-colors duration-300" style={{ backgroundColor: theme.primary_color, color: '#ffffff' }}>
                <div className="max-w-7xl mx-auto relative z-10">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4">
                        {activeCollectionName ? activeCollectionName : 'Shop'}
                    </h1>
                    <p className="text-base md:text-lg opacity-90 max-w-2xl">
                        {activeCollectionName
                            ? `Explore our ${activeCollectionName} collection.`
                            : 'Browse our exclusive collection of premium products.'}
                    </p>
                </div>
                {/* Optional: Add a subtle pattern if desired, or keep it clean */}
            </div>

            {/* Trust Badges - Platform Level */}
            <TrustBadges theme={theme} storeAppearance={storeAppearance} />

            {/* Breadcrumb & Top Bar */}
            <div className="border-b py-4 px-4 lg:px-8 bg-white/50 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70">
                        <Link to={storePath(subdomain, "/")} className="hover:underline">Home</Link>
                        <ArrowRight size={10} />
                        <span className="font-bold">Shop</span>
                        {activeCollectionName && (
                            <>
                                <ArrowRight size={10} />
                                <span className="font-bold" style={{ color: theme.accent_color }}>{activeCollectionName}</span>
                            </>
                        )}
                    </div>

                    <Button
                        variant="outline"
                        size="sm"
                        className="md:hidden flex items-center gap-2"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    >
                        <Filter size={16} />
                        {isSidebarOpen ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row gap-8 lg:gap-12">

                    {/* Left Sidebar */}
                    <aside className={cn(
                        "md:w-64 flex-shrink-0 transition-all duration-300",
                        isSidebarOpen ? "block" : "hidden md:block"
                    )}>
                        <div className="sticky top-28">
                            <div className="p-4 rounded-t-lg shadow-sm text-white" style={{ backgroundColor: theme.primary_color }}>
                                <h3 className="font-bold text-lg tracking-wide">Collections</h3>
                            </div>
                            <div className="bg-white shadow-sm border border-t-0 border-slate-100">
                                <div className="p-2 border-b border-slate-100">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search collections..."
                                            value={collectionSearch}
                                            onChange={(e) => setCollectionSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-slate-100 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                                {filteredCollections.length === 0 && (
                                    <div className="p-4 text-sm opacity-50 text-center">No collections found</div>
                                )}
                                {filteredCollections.map((collection) => {
                                    const id = collection.collection_id || collection._id;
                                    const isActive = activeCollection === id;
                                    return (
                                        <button
                                            key={id}
                                            onClick={() => handleCollectionClick(collection)}
                                            className={cn(
                                                "w-full text-left px-4 py-3 text-sm font-medium transition-colors border-l-4 hover:bg-slate-50",
                                                isActive ? "bg-slate-50" : "border-transparent opacity-70 hover:opacity-100"
                                            )}
                                            style={{
                                                borderLeftColor: isActive ? theme.accent_color : 'transparent',
                                                color: isActive ? theme.accent_color : theme.primary_color
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span>{collection.name}</span>
                                                {isActive && <ArrowRight size={14} />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                        </div>
                    </aside>

                    {/* Product Grid */}
                    <div className="flex-1 min-w-0">
                        {/* Active Filter Tags */}
                        <div className="flex flex-wrap gap-2 mb-6 animate-in fade-in slide-in-from-top-2">
                            {activeCollectionName && (
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold shadow-sm border" style={{ backgroundColor: `${theme.accent_color}20`, color: theme.accent_color, borderColor: `${theme.accent_color}40` }}>
                                    Collection: {activeCollectionName}
                                    <button onClick={() => { setActiveCollection(null); setActiveCollectionName(""); navigate(storePath(subdomain, `/shop${searchTerm ? '?search=' + encodeURIComponent(searchTerm) : ''}`)); }} className="hover:text-red-600 p-0.5 rounded-full transition-colors">
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            {searchTerm && (
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold shadow-sm border bg-slate-100 text-slate-700 border-slate-200">
                                    Search: "{searchTerm}"
                                    <button onClick={() => { setSearchTerm(""); navigate(storePath(subdomain, `/shop${activeCollection ? '?collection_id=' + activeCollection : ''}`)); }} className="hover:text-red-600 p-0.5 rounded-full transition-colors">
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                        </div>

                        {loading && page === 1 && products.length === 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl p-4 h-80 animate-pulse border border-slate-100"></div>
                                ))}
                            </div>
                        ) : products.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {products.map((product, index) => {
                                    const isLast = index === products.length - 1;
                                    return (
                                        <div
                                            key={`${product.product_id}-${index}`}
                                            ref={isLast ? lastProductElementRef : null}
                                            className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 group border border-slate-50 flex flex-col"
                                        >
                                            <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-slate-100 mb-4">
                                                <Link to={storePath(subdomain, `/product/${product.product_id}`)}>
                                                    {product.image_url || product.images?.[0] ? (
                                                        <img
                                                            src={getImageUrl(product.image_url || product.images?.[0])}
                                                            alt={product.name}
                                                            className="w-full h-full object-contain bg-white group-hover:scale-105 transition-transform duration-500 p-2"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400">
                                                            <Store size={32} />
                                                        </div>
                                                    )}
                                                </Link>
                                                {/* Stock / Availability Badge */}
                                                {(product.is_active === false) && (
                                                    <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur text-xs font-bold rounded-md shadow-sm text-red-600">
                                                        Not Available
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-2 flex-grow flex flex-col">
                                                <Link to={storePath(subdomain, `/product/${product.product_id}`)} className="block flex-grow">
                                                    <h3 className="font-semibold line-clamp-2 transition-colors" style={{ fontSize: '1.125rem', color: theme.primary_color }}>
                                                        {product.name}
                                                    </h3>
                                                </Link>
                                                <div className="pt-2">
                                                    <span className="font-bold text-lg" style={{ color: theme.accent_color }}>
                                                        ${(product.display_price || product.calculated_price || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                                    <Store size={32} />
                                </div>
                                <h3 className="text-lg font-bold">No products found</h3>
                                <Button variant="outline" onClick={clearFilter} className="mt-4">Clear Filters</Button>
                            </div>
                        )}

                        {/* Infinite Scroll Loader */}
                        {loading && page > 1 && (
                            <div className="py-8 flex justify-center">
                                <Loader2 size={24} className="animate-spin opacity-50" />
                            </div>
                        )}

                        {!hasMore && products.length > 0 && (
                            <div className="py-12 text-center text-sm font-medium uppercase tracking-widest opacity-50 border-t border-slate-100 mt-12 mx-auto max-w-xs">
                                End of List
                            </div>
                        )}
                    </div>
                </div>
            </main>

        </div>
    );
}
